import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { format, startOfMonth, endOfMonth, startOfDay, endOfDay } from 'date-fns';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { TableSkeleton } from '../components/ui/Skeleton';
import { formatCurrency, cn, fmtDate } from '../lib/utils';
import toast from 'react-hot-toast';
import * as Lu from 'react-icons/lu';
import SafeIcon from '../common/SafeIcon';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

const CHARGE_STATUS = {
  chargeable: { label: 'Chargeable', color: 'bg-rose-50 text-rose-600 border-rose-200' },
  waived: { label: 'Waived', color: 'bg-amber-50 text-amber-600 border-amber-200' },
  no_charge: { label: 'No Charge', color: 'bg-slate-50 text-slate-400 border-slate-200' },
};

function ChargeToggle({ value, onChange }) {
  const options = ['chargeable', 'waived', 'no_charge'];
  return (
    <div className="flex rounded-lg overflow-hidden border text-[8px] font-black uppercase">
      {options.map(opt => (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt)}
          className={cn(
            'px-2 py-1 transition-all',
            value === opt ? CHARGE_STATUS[opt].color : 'bg-white text-slate-300'
          )}
        >
          {CHARGE_STATUS[opt].label}
        </button>
      ))}
    </div>
  );
}

export default function Invoices() {
  const [data, setData] = useState({ list: [], students: [], loading: true });
  const [ui, setUi] = useState({
    search: '', filter: 'unpaid', genOpen: false, genStep: 1,
    preview: null, previewData: { groups: [] },
    isEditingRemarks: false, tempRemarks: ''
  });
  const [form, setForm] = useState({
    student: null,
    mode: 'combined',
    range: {
      start: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
      end: format(endOfMonth(new Date()), 'yyyy-MM-dd')
    },
    groups: [],
    remarks: '',
    processing: false
  });

  const fetchData = async () => {
    const [inv, st] = await Promise.all([
      supabase
        .from('invoices')
        .select('*,students(*)')
        .order('created_at', { ascending: false }),
      supabase.from('students').select('id,full_name,student_id_code').order('full_name')
    ]);
    setData({ list: inv.data || [], students: st.data || [], loading: false });
  };

  useEffect(() => { fetchData(); }, []);

  // Fetch subject/level info per invoice by looking at its attendance records
  const fetchInvoiceSubjects = async (invoiceId) => {
    const { data: att } = await supabase
      .from('attendance')
      .select('subjects(name,level)')
      .eq('invoice_id', invoiceId);
    if (!att) return [];
    const seen = new Set();
    const subjects = [];
    att.forEach(a => {
      const key = `${a.subjects?.name}-${a.subjects?.level}`;
      if (!seen.has(key) && a.subjects?.name) {
        seen.add(key);
        subjects.push({ name: a.subjects.name, level: a.subjects.level });
      }
    });
    return subjects;
  };

  const fetchDetails = async (id) => {
    const { data: att } = await supabase
      .from('attendance')
      .select('id,timestamp,subjects(*),attendance_type,status')
      .eq('invoice_id', id)
      .order('timestamp', { ascending: true });
    const { data: port } = await supabase
      .from('student_portfolios')
      .select('*,subjects(name)')
      .in('attendance_id', att?.map(a => a.id) || []);

    const subjectMap = {};
    (att || []).forEach(a => {
      const subId = a.subjects?.id || 'unknown';
      if (!subjectMap[subId]) subjectMap[subId] = { subject: a.subjects, records: [] };
      subjectMap[subId].records.push(a);
    });
    const groups = Object.values(subjectMap);
    return { groups, port: port || [] };
  };

  const handlePreview = async (inv) => {
    const l = toast.loading("Loading Preview...");
    const details = await fetchDetails(inv.id);
    setUi({ ...ui, preview: inv, previewData: details, tempRemarks: inv.overall_remarks || '', isEditingRemarks: false });
    toast.dismiss(l);
  };

  const updateRemarks = async () => {
    const l = toast.loading("Updating summary...");
    try {
      await supabase.from('invoices').update({ overall_remarks: ui.tempRemarks }).eq('id', ui.preview.id);
      setUi({ ...ui, preview: { ...ui.preview, overall_remarks: ui.tempRemarks }, isEditingRemarks: false });
      toast.success("Summary updated", { id: l });
      fetchData();
    } catch (e) { toast.error("Update failed", { id: l }); }
  };

  const markAsPaid = async (inv) => {
    const l = toast.loading("Marking as paid...");
    try {
      await supabase.from('invoices').update({ status: 'paid' }).eq('id', inv.id);
      toast.success("Invoice marked as paid!", { id: l });
      fetchData();
    } catch (e) { toast.error(e.message, { id: l }); }
  };

  const markAsUnpaid = async (inv) => {
    const l = toast.loading("Reversing payment status...");
    try {
      await supabase.from('invoices').update({ status: 'unpaid' }).eq('id', inv.id);
      toast.success("Invoice reverted to unpaid.", { id: l });
      fetchData();
    } catch (e) { toast.error(e.message, { id: l }); }
  };

  const deleteInvoice = async (inv) => {
    if (!confirm("Are you sure? This will revert all sessions to 'Unbilled' status so you can regenerate this invoice.")) return;
    const l = toast.loading("Reverting sessions...");
    try {
      await supabase.from('attendance').update({ invoice_id: null }).eq('invoice_id', inv.id);
      await supabase.from('invoices').delete().eq('id', inv.id);
      toast.success("Invoice removed. Sessions are now unbilled.", { id: l });
      fetchData();
    } catch (e) { toast.error(e.message, { id: l }); }
  };

  const fetchAttForGen = async () => {
    if (!form.student) return;
    setForm(prev => ({ ...prev, processing: true }));
    try {
      const { data: res } = await supabase
        .from('attendance')
        .select('*,subjects(*)')
        .eq('student_id', form.student.id)
        .is('invoice_id', null)
        .gte('timestamp', startOfDay(new Date(form.range.start)).toISOString())
        .lte('timestamp', endOfDay(new Date(form.range.end)).toISOString())
        .order('timestamp', { ascending: true });

      const subjectMap = {};
      (res || []).forEach(a => {
        const subId = a.subjects?.id || 'unknown';
        if (!subjectMap[subId]) {
          subjectMap[subId] = { subject: a.subjects, records: [] };
        }
        const isAbsenceType = a.attendance_type === 'absent' || a.attendance_type === 'no_class';
        subjectMap[subId].records.push({
          att: a,
          included: true,
          chargeStatus: isAbsenceType ? 'no_charge' : 'chargeable'
        });
      });

      const groups = Object.values(subjectMap);
      setForm(prev => ({ ...prev, groups, processing: false }));
      setUi(prev => ({ ...prev, genStep: 2 }));
    } catch (e) {
      toast.error("Failed to fetch records");
      setForm(prev => ({ ...prev, processing: false }));
    }
  };

  const calcGroupTotal = (group) =>
    group.records
      .filter(r => r.included && r.chargeStatus === 'chargeable')
      .reduce((s, r) => s + (r.att.subjects?.rate || 0), 0);

  const calcGrandTotal = () =>
    form.groups.reduce((s, g) => s + calcGroupTotal(g), 0);

  const toggleRecord = (gIdx, rIdx) => {
    const groups = form.groups.map((g, gi) => gi !== gIdx ? g : {
      ...g,
      records: g.records.map((r, ri) => ri !== rIdx ? r : { ...r, included: !r.included })
    });
    setForm(p => ({ ...p, groups }));
  };

  const setChargeStatus = (gIdx, rIdx, status) => {
    const groups = form.groups.map((g, gi) => gi !== gIdx ? g : {
      ...g,
      records: g.records.map((r, ri) => ri !== rIdx ? r : { ...r, chargeStatus: status })
    });
    setForm(p => ({ ...p, groups }));
  };

  const finalize = async () => {
    setForm(prev => ({ ...prev, processing: true }));
    try {
      const groupsToInvoice = form.mode === 'separate'
        ? form.groups.map(g => [g])
        : [form.groups];

      for (const grp of groupsToInvoice) {
        const allRecords = grp.flatMap(g => g.records);
        const chargeableRecords = allRecords.filter(r => r.included && r.chargeStatus === 'chargeable');
        const includedRecords = allRecords.filter(r => r.included);
        const total = chargeableRecords.reduce((s, r) => s + (r.att.subjects?.rate || 0), 0);

        const { data: inv, error } = await supabase.from('invoices').insert([{
          student_id: form.student.id,
          amount: total,
          billing_period_start: form.range.start,
          billing_period_end: form.range.end,
          status: 'unpaid',
          overall_remarks: form.remarks
        }]).select().single();

        if (error) throw error;

        const allIncludedIds = includedRecords.map(r => r.att.id);
        if (allIncludedIds.length > 0) {
          await supabase.from('attendance').update({ invoice_id: inv.id }).in('id', allIncludedIds);
        }
      }

      toast.success(form.mode === 'separate'
        ? `${form.groups.length} separate invoices generated!`
        : "Invoice Generated!");
      setUi(prev => ({ ...prev, genOpen: false }));
      fetchData();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setForm(prev => ({ ...prev, processing: false }));
    }
  };

  const genPDF = async (inv, groups, portData) => {
    const l = toast.loading("Building PDF...");
    const doc = new jsPDF();
    const primary = [79, 70, 229];

    doc.setFillColor(...primary);
    doc.rect(0, 0, 210, 50, 'F');
    doc.setTextColor(255);
    doc.setFontSize(26); doc.setFont('helvetica', 'bold');
    doc.text('HAPPYNESTLING', 20, 22);
    doc.setFontSize(10); doc.setFont('helvetica', 'normal');
    doc.text('ACADEMIC CENTRE', 20, 28);
    doc.setFontSize(20); doc.setFont('helvetica', 'bold');
    doc.text('STATEMENT', 190, 22, { align: 'right' });
    doc.setFontSize(9); doc.setFont('helvetica', 'normal');
    doc.text(`#S-${inv.id.slice(0, 8).toUpperCase()}`, 190, 28, { align: 'right' });

    doc.setTextColor(100, 116, 139);
    doc.setFontSize(10);
    doc.text('RECIPIENT', 20, 68);
    doc.setTextColor(...primary);
    doc.setFontSize(16); doc.setFont('helvetica', 'bold');
    doc.text(inv.students.full_name.toUpperCase(), 20, 76);
    doc.setTextColor(100, 116, 139);
    doc.setFontSize(10); doc.setFont('helvetica', 'normal');
    doc.text('BILLING CYCLE', 190, 68, { align: 'right' });
    doc.setTextColor(30, 41, 59);
    doc.setFontSize(11); doc.setFont('helvetica', 'bold');
    doc.text(`${fmtDate(inv.billing_period_start, 'dd MMM')} - ${fmtDate(inv.billing_period_end, 'dd MMM yyyy')}`, 190, 76, { align: 'right' });

    let currentY = 90;

    for (const group of groups) {
      doc.setTextColor(...primary);
      doc.setFontSize(11); doc.setFont('helvetica', 'bold');
      doc.text(group.subject?.name?.toUpperCase() || 'SUBJECT', 20, currentY);
      currentY += 4;

      const rows = group.records.map(r => [
        fmtDate(r.timestamp, 'dd MMM yyyy, HH:mm'),
        r.attendance_type?.replace('_', ' ').toUpperCase() || 'REGULAR',
        formatCurrency(r.subjects?.rate || 0)
      ]);

      doc.autoTable({
        startY: currentY,
        head: [['DATE & TIME', 'TYPE', 'FEE']],
        body: rows,
        theme: 'grid',
        headStyles: { fillColor: primary, fontSize: 9, fontStyle: 'bold' },
        bodyStyles: { fontSize: 9, textColor: [30, 41, 59] },
        columnStyles: { 2: { halign: 'right' } },
        margin: { left: 20, right: 20 }
      });

      currentY = doc.lastAutoTable.finalY + 6;
      const groupTotal = group.records.reduce((s, r) => s + (r.subjects?.rate || 0), 0);
      doc.setTextColor(100, 116, 139);
      doc.setFontSize(9); doc.setFont('helvetica', 'normal');
      doc.text(`${group.subject?.name} Subtotal:`, 150, currentY, { align: 'right' });
      doc.setTextColor(...primary);
      doc.setFont('helvetica', 'bold');
      doc.text(formatCurrency(groupTotal), 190, currentY, { align: 'right' });
      currentY += 10;
    }

    doc.setTextColor(100, 116, 139);
    doc.setFontSize(10); doc.setFont('helvetica', 'normal');
    doc.text('TOTAL AMOUNT DUE', 190, currentY + 8, { align: 'right' });
    doc.setTextColor(...primary);
    doc.setFontSize(26); doc.setFont('helvetica', 'bold');
    doc.text(formatCurrency(inv.amount), 190, currentY + 20, { align: 'right' });

    if (inv.overall_remarks) {
      const remarkY = currentY + 35;
      doc.setFillColor(245, 247, 255);
      doc.rect(20, remarkY, 170, 30, 'F');
      doc.setTextColor(...primary);
      doc.setFontSize(9); doc.setFont('helvetica', 'bold');
      doc.text('TEACHER SUMMARY', 25, remarkY + 8);
      doc.setTextColor(71, 85, 105);
      doc.setFont('helvetica', 'italic'); doc.setFontSize(10);
      doc.text(doc.splitTextToSize(inv.overall_remarks, 160), 25, remarkY + 15);
    }

    if (portData && portData.length > 0) {
      doc.addPage();
      doc.setFillColor(30, 41, 59);
      doc.rect(0, 0, 210, 40, 'F');
      doc.setTextColor(255);
      doc.setFontSize(18); doc.setFont('helvetica', 'bold');
      doc.text('LEARNING PORTFOLIO', 20, 25);
      doc.setFontSize(9); doc.setFont('helvetica', 'normal');
      doc.text(`SESSION LOGS FOR ${inv.students.full_name.toUpperCase()}`, 20, 31);
      doc.autoTable({
        startY: 50,
        head: [['SUBJECT', 'HOMEWORK', 'LESSON NOTES', 'SCORE']],
        body: portData.map(p => [p.subjects?.name || 'N/A', p.homework_status, doc.splitTextToSize(p.notes || '-', 60), `${p.score}/5`]),
        headStyles: { fillColor: [30, 41, 59] },
        bodyStyles: { fontSize: 8, textColor: [51, 65, 85] }
      });
    }

    doc.save(`Invoice_${inv.students.full_name}_${fmtDate(new Date(), 'ddMM')}.pdf`);
    toast.success("Downloaded", { id: l });
  };

  const unpaidList = data.list.filter(i =>
    i.status === 'unpaid' &&
    i.students?.full_name.toLowerCase().includes(ui.search.toLowerCase())
  );

  const paidList = data.list.filter(i =>
    i.status === 'paid' &&
    i.students?.full_name.toLowerCase().includes(ui.search.toLowerCase())
  );

  const grandTotal = calcGrandTotal();

  const InvoiceRow = ({ i, isPaid }) => (
    <tr className={cn("hover:bg-indigo-50/30 transition-colors", isPaid && "opacity-80")}>
      {/* Learner */}
      <td className="px-4 py-4 sm:px-6">
        <p className="font-bold text-[11px] sm:text-sm uppercase dark:text-white">{i.students?.full_name}</p>
        <p className="text-[8px] font-bold text-slate-400">
          {fmtDate(i.billing_period_start, 'MMM d')} – {fmtDate(i.billing_period_end, 'MMM d, yyyy')}
        </p>
      </td>
      {/* Subject & Level — loaded lazily via a sub-component */}
      <td className="px-4 py-4 sm:px-6 hidden md:table-cell">
        <InvoiceSubjectCell invoiceId={i.id} />
      </td>
      {/* Fee */}
      <td className="px-4 py-4 sm:px-6 text-right font-black text-indigo-600 text-xs sm:text-base whitespace-nowrap">
        {formatCurrency(i.amount)}
      </td>
      {/* Status badge */}
      <td className="px-4 py-4 sm:px-6 hidden sm:table-cell">
        <Badge variant={isPaid ? 'success' : 'warning'} className="text-[8px] uppercase font-black">
          {isPaid ? 'Paid' : 'Unpaid'}
        </Badge>
      </td>
      {/* Actions */}
      <td className="px-4 py-4 sm:px-6 text-right">
        <div className="flex justify-end gap-1 flex-wrap">
          <button
            onClick={() => handlePreview(i)}
            title="Preview"
            className="p-2 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition-colors"
          >
            <SafeIcon icon={Lu.LuEye} size={14} />
          </button>
          {!isPaid ? (
            <>
              <button
                onClick={() => markAsPaid(i)}
                title="Mark as Paid"
                className="p-2 bg-emerald-50 text-emerald-600 rounded-lg hover:bg-emerald-100 transition-colors"
              >
                <SafeIcon icon={Lu.LuCheckCircle} size={14} />
              </button>
              <button
                onClick={() => deleteInvoice(i)}
                title="Delete Invoice"
                className="p-2 bg-rose-50 text-rose-600 rounded-lg hover:bg-rose-100 transition-colors"
              >
                <SafeIcon icon={Lu.LuTrash2} size={14} />
              </button>
            </>
          ) : (
            <button
              onClick={() => markAsUnpaid(i)}
              title="Revert to Unpaid"
              className="p-2 bg-amber-50 text-amber-600 rounded-lg hover:bg-amber-100 transition-colors"
            >
              <SafeIcon icon={Lu.LuUndo2} size={14} />
            </button>
          )}
        </div>
      </td>
    </tr>
  );

  return (
    <div className="space-y-6 pb-32 mobile-tight">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl md:text-5xl font-black dark:text-white uppercase tracking-tighter flex items-center gap-2">
          <div className="p-2 bg-indigo-600 text-white rounded-xl shadow-lg">
            <SafeIcon icon={Lu.LuReceipt} size={24} />
          </div>
          Billing
        </h1>
        <Button onClick={() => setUi({ ...ui, genOpen: true, genStep: 1 })} className="w-full sm:w-auto bg-indigo-600 rounded-2xl">
          <SafeIcon icon={Lu.LuPlus} className="mr-2" /> New Statement
        </Button>
      </div>

      {/* Search */}
      <div className="bg-white dark:bg-slate-900 p-4 rounded-3xl border shadow-sm">
        <input
          placeholder="Search by learner name..."
          value={ui.search}
          onChange={e => setUi({ ...ui, search: e.target.value })}
          className="w-full p-3 bg-slate-50 dark:bg-slate-800 rounded-xl font-bold outline-none text-[10px]"
        />
      </div>

      {/* Unpaid Invoices */}
      <div className="bg-white dark:bg-slate-900 rounded-3xl border overflow-hidden shadow-sm">
        <div className="flex items-center gap-3 px-6 py-4 border-b bg-slate-50 dark:bg-slate-800">
          <div className="w-2.5 h-2.5 bg-amber-400 rounded-full" />
          <h2 className="text-[10px] font-black uppercase tracking-widest text-slate-600 dark:text-slate-300">
            Outstanding — {unpaidList.length} invoice{unpaidList.length !== 1 ? 's' : ''}
          </h2>
        </div>
        {data.loading ? <TableSkeleton rows={4} /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-50 dark:bg-slate-800 font-black text-[8px] uppercase text-slate-400 tracking-widest border-b">
                <tr>
                  <th className="px-4 py-3 sm:px-6">Learner</th>
                  <th className="px-4 py-3 sm:px-6 hidden md:table-cell">Subject / Level</th>
                  <th className="px-4 py-3 sm:px-6 text-right">Fee</th>
                  <th className="px-4 py-3 sm:px-6 hidden sm:table-cell">Status</th>
                  <th className="px-4 py-3 sm:px-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {unpaidList.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-10 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center">
                          <SafeIcon icon={Lu.LuCheckCircle} size={24} className="text-emerald-500" />
                        </div>
                        <p className="text-[10px] font-black uppercase text-slate-400">All invoices are settled!</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  unpaidList.map(i => <InvoiceRow key={i.id} i={i} isPaid={false} />)
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Paid Invoices */}
      <div className="bg-white dark:bg-slate-900 rounded-3xl border overflow-hidden shadow-sm">
        <div className="flex items-center gap-3 px-6 py-4 border-b bg-slate-50 dark:bg-slate-800">
          <div className="w-2.5 h-2.5 bg-emerald-400 rounded-full" />
          <h2 className="text-[10px] font-black uppercase tracking-widest text-slate-600 dark:text-slate-300">
            Paid — {paidList.length} invoice{paidList.length !== 1 ? 's' : ''}
          </h2>
          <span className="ml-auto text-[9px] font-bold text-slate-400 uppercase">
            Total collected: {formatCurrency(paidList.reduce((s, i) => s + (i.amount || 0), 0))}
          </span>
        </div>
        {data.loading ? <TableSkeleton rows={3} /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-50 dark:bg-slate-800 font-black text-[8px] uppercase text-slate-400 tracking-widest border-b">
                <tr>
                  <th className="px-4 py-3 sm:px-6">Learner</th>
                  <th className="px-4 py-3 sm:px-6 hidden md:table-cell">Subject / Level</th>
                  <th className="px-4 py-3 sm:px-6 text-right">Fee</th>
                  <th className="px-4 py-3 sm:px-6 hidden sm:table-cell">Status</th>
                  <th className="px-4 py-3 sm:px-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {paidList.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-[10px] font-black uppercase text-slate-300">
                      No paid invoices yet
                    </td>
                  </tr>
                ) : (
                  paidList.map(i => <InvoiceRow key={i.id} i={i} isPaid={true} />)
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Preview Modal */}
      <Modal isOpen={!!ui.preview} onClose={() => setUi({ ...ui, preview: null })} title="Statement Preview" className="max-w-4xl max-h-[95vh] overflow-y-auto p-1 sm:p-6">
        {ui.preview && (
          <div className="space-y-6">
            <div className="pdf-preview-container border border-slate-200 rounded-3xl overflow-hidden shadow-2xl bg-white text-slate-900 mx-auto max-w-[800px]">
              <div className="bg-indigo-600 p-6 sm:p-12 text-white flex flex-col sm:flex-row justify-between gap-4">
                <div><h2 className="text-xl sm:text-4xl font-black">HAPPYNESTLING</h2><p className="text-[8px] sm:text-[10px] font-bold opacity-80 uppercase tracking-widest">Academic Centre</p></div>
                <div className="text-left sm:text-right"><h3 className="text-lg sm:text-2xl font-black uppercase">Statement</h3><p className="text-[9px] font-mono opacity-80">#S-{ui.preview.id.slice(0, 8).toUpperCase()}</p></div>
              </div>
              <div className="p-6 sm:p-12 space-y-8">
                <div className="flex flex-col sm:flex-row justify-between gap-4">
                  <div>
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Recipient</p>
                    <p className="text-lg sm:text-2xl font-black uppercase text-indigo-600">{ui.preview.students.full_name}</p>
                  </div>
                  <div className="text-left sm:text-right">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Billing Cycle</p>
                    <p className="text-xs sm:text-sm font-black">{fmtDate(ui.preview.billing_period_start, 'dd MMM')} - {fmtDate(ui.preview.billing_period_end, 'dd MMM yyyy')}</p>
                  </div>
                </div>

                {ui.previewData.groups.map((group, gi) => (
                  <div key={gi}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 bg-indigo-500 rounded-full" />
                      <p className="text-[10px] font-black uppercase text-indigo-600 tracking-widest">{group.subject?.name}</p>
                      {group.subject?.level && (
                        <Badge variant="primary" className="text-[7px] uppercase">{group.subject.level}</Badge>
                      )}
                    </div>
                    <div className="rounded-2xl border border-slate-100 overflow-hidden mb-2">
                      <table className="w-full text-left text-[10px] sm:text-sm">
                        <thead className="bg-slate-50 font-black text-[8px] text-slate-400 uppercase">
                          <tr><th className="px-4 py-3">Date & Time</th><th className="px-4 py-3">Type</th><th className="px-4 py-3 text-right">Fee</th></tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                          {group.records.map((r, ri) => (
                            <tr key={ri}>
                              <td className="px-4 py-3 font-bold">{fmtDate(r.timestamp, 'dd MMM yyyy, HH:mm')}</td>
                              <td className="px-4 py-3">
                                <span className="text-[8px] font-black uppercase text-slate-500">{r.attendance_type?.replace('_', ' ')}</span>
                              </td>
                              <td className="px-4 py-3 text-right font-black">{formatCurrency(r.subjects?.rate || 0)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="text-right text-[10px] font-black text-indigo-600 pr-1">
                      Subtotal: {formatCurrency(group.records.reduce((s, r) => s + (r.subjects?.rate || 0), 0))}
                    </div>
                  </div>
                ))}

                <div className="text-right border-t pt-6">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Amount Due</p>
                  <p className="text-3xl sm:text-5xl font-black text-indigo-600">{formatCurrency(ui.preview.amount)}</p>
                </div>

                <div className="bg-indigo-50 p-6 rounded-3xl border border-indigo-100 relative group">
                  <div className="flex justify-between mb-3">
                    <h4 className="text-[9px] font-black text-indigo-600 uppercase tracking-widest">Teacher Summary</h4>
                    <button onClick={() => setUi({ ...ui, isEditingRemarks: !ui.isEditingRemarks })} className="text-[9px] font-bold text-indigo-400 hover:text-indigo-600 uppercase">
                      {ui.isEditingRemarks ? 'Cancel' : 'Edit'}
                    </button>
                  </div>
                  {ui.isEditingRemarks ? (
                    <div className="space-y-3">
                      <textarea value={ui.tempRemarks} onChange={e => setUi({ ...ui, tempRemarks: e.target.value })}
                        className="w-full bg-white p-3 rounded-xl text-xs outline-none min-h-[80px]" />
                      <Button onClick={updateRemarks} className="w-full py-2 text-[10px]">Save Updates</Button>
                    </div>
                  ) : (
                    <p className="text-xs sm:text-sm italic text-slate-600 leading-relaxed">"{ui.preview.overall_remarks || 'No summary provided.'}"</p>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => genPDF(ui.preview, ui.previewData.groups, ui.previewData.port)} className="flex-1 py-4 font-bold bg-indigo-600">Download PDF</Button>
              <Button variant="ghost" onClick={() => setUi({ ...ui, preview: null })} className="flex-1 py-4">Close</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Generate Invoice Modal */}
      <Modal isOpen={ui.genOpen} onClose={() => setUi({ ...ui, genOpen: false })} title="Generate Statement" className="max-w-3xl">
        <div className="space-y-6 p-2">
          <div className="flex justify-between px-4">
            {[1, 2, 3].map(s => (
              <div key={s} className={cn("w-8 h-8 rounded-xl flex items-center justify-center text-xs font-black transition-all",
                ui.genStep >= s ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-400")}>
                {s}
              </div>
            ))}
          </div>

          {ui.genStep === 1 && (
            <div className="space-y-4">
              <select className="w-full bg-slate-50 p-4 rounded-2xl font-bold text-xs outline-none border"
                onChange={e => setForm({ ...form, student: data.students.find(s => s.id === e.target.value) })}>
                <option value="">Select Student...</option>
                {data.students.map(s => <option key={s.id} value={s.id}>{s.full_name}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-3">
                <Input type="date" label="Start" value={form.range.start} onChange={e => setForm({ ...form, range: { ...form.range, start: e.target.value } })} />
                <Input type="date" label="End" value={form.range.end} onChange={e => setForm({ ...form, range: { ...form.range, end: e.target.value } })} />
              </div>
              <div className="space-y-2">
                <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">Invoice Mode</p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { value: 'combined', icon: Lu.LuLayers, label: 'Combined', desc: 'One invoice, grouped by subject' },
                    { value: 'separate', icon: Lu.LuFiles, label: 'Separate', desc: 'Individual invoice per subject' }
                  ].map(opt => (
                    <button key={opt.value} type="button" onClick={() => setForm(p => ({ ...p, mode: opt.value }))}
                      className={cn("p-4 rounded-2xl border-2 text-left transition-all",
                        form.mode === opt.value ? "border-indigo-600 bg-indigo-50" : "border-slate-100 hover:border-slate-200 bg-white")}>
                      <SafeIcon icon={opt.icon} size={20} className={form.mode === opt.value ? "text-indigo-600" : "text-slate-400"} />
                      <p className={cn("font-black text-xs uppercase mt-2", form.mode === opt.value ? "text-indigo-700" : "text-slate-700")}>{opt.label}</p>
                      <p className="text-[9px] text-slate-400 mt-0.5">{opt.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
              <Button disabled={!form.student || form.processing} onClick={fetchAttForGen} className="w-full py-4 text-xs font-black uppercase bg-indigo-600">
                Fetch Records
              </Button>
            </div>
          )}

          {ui.genStep === 2 && (
            <div className="space-y-4">
              <div className="bg-indigo-50 p-5 rounded-3xl border border-indigo-100 flex justify-between items-center">
                <div>
                  <p className="text-[9px] font-black text-indigo-600 uppercase tracking-widest">Total Chargeable</p>
                  <p className="text-3xl font-black text-indigo-900">{formatCurrency(grandTotal)}</p>
                </div>
                <div className="text-right">
                  <p className="text-[9px] font-black text-slate-400 uppercase">Mode</p>
                  <Badge variant="primary" className="text-[8px] uppercase">{form.mode}</Badge>
                </div>
              </div>

              <div className="max-h-[400px] overflow-y-auto space-y-6 pr-1">
                {form.groups.map((group, gIdx) => (
                  <div key={gIdx} className="bg-white rounded-2xl border overflow-hidden">
                    <div className="bg-slate-50 px-4 py-3 flex items-center justify-between border-b">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-indigo-500 rounded-full" />
                        <p className="font-black text-xs uppercase text-slate-700">{group.subject?.name}</p>
                        {group.subject?.level && (
                          <Badge variant="primary" className="text-[7px] uppercase">{group.subject.level}</Badge>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-[9px] font-black text-indigo-600">{formatCurrency(calcGroupTotal(group))}</p>
                        <p className="text-[8px] text-slate-400 uppercase">{group.records.filter(r => r.included && r.chargeStatus === 'chargeable').length} chargeable</p>
                      </div>
                    </div>
                    <div className="divide-y">
                      {group.records.map((rec, rIdx) => {
                        const isAbsenceType = rec.att.attendance_type === 'absent' || rec.att.attendance_type === 'no_class';
                        return (
                          <div key={rIdx} className={cn("px-4 py-3 transition-colors", !rec.included && "opacity-40")}>
                            <div className="flex items-start gap-3">
                              <input type="checkbox" checked={rec.included}
                                onChange={() => toggleRecord(gIdx, rIdx)}
                                className="mt-1 rounded accent-indigo-600 shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-1.5 mb-1">
                                  <p className="text-[10px] font-black uppercase text-slate-700">
                                    {fmtDate(rec.att.timestamp, 'dd MMM yyyy')}
                                  </p>
                                  <p className="text-[9px] text-slate-400">{fmtDate(rec.att.timestamp, 'h:mm a')}</p>
                                  <Badge
                                    variant={rec.att.attendance_type === 'absent' || rec.att.attendance_type === 'no_class' ? 'danger' : 'default'}
                                    className="text-[7px] uppercase"
                                  >
                                    {rec.att.attendance_type?.replace('_', ' ')}
                                  </Badge>
                                </div>
                                {isAbsenceType && rec.included && (
                                  <ChargeToggle value={rec.chargeStatus} onChange={s => setChargeStatus(gIdx, rIdx, s)} />
                                )}
                              </div>
                              <p className={cn("text-xs font-black shrink-0",
                                rec.chargeStatus === 'chargeable' ? "text-indigo-600" : "text-slate-300 line-through")}>
                                {formatCurrency(rec.att.subjects?.rate || 0)}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-2 pt-2">
                <Button variant="ghost" onClick={() => setUi({ ...ui, genStep: 1 })} className="flex-1">Back</Button>
                <Button onClick={() => setUi({ ...ui, genStep: 3 })} className="flex-[2] bg-indigo-600">Next</Button>
              </div>
            </div>
          )}

          {ui.genStep === 3 && (
            <div className="space-y-4">
              <div className="bg-indigo-50 p-5 rounded-3xl border border-indigo-100">
                <p className="text-[9px] font-black text-indigo-600 uppercase tracking-widest mb-1">Summary</p>
                <p className="text-2xl font-black text-indigo-900">{formatCurrency(grandTotal)}</p>
                <p className="text-[9px] text-slate-500 mt-1">
                  {form.mode === 'separate'
                    ? `${form.groups.length} separate invoice(s) will be created`
                    : '1 combined invoice with subject groupings'}
                </p>
              </div>
              <textarea value={form.remarks} onChange={e => setForm({ ...form, remarks: e.target.value })}
                placeholder="Teacher feedback / summary for this billing period..."
                className="w-full bg-slate-50 p-5 rounded-3xl outline-none min-h-[120px] text-xs font-medium border-2 border-transparent focus:border-indigo-100" />
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => setUi({ ...ui, genStep: 2 })} className="flex-1">Back</Button>
                <Button disabled={form.processing} onClick={finalize} className="flex-[2] bg-indigo-600">
                  Finalize & Issue
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}

// Lazy-loads subject/level tags for a given invoice
function InvoiceSubjectCell({ invoiceId }) {
  const [subjects, setSubjects] = useState(null);

  useEffect(() => {
    let cancelled = false;
    supabase
      .from('attendance')
      .select('subjects(name,level)')
      .eq('invoice_id', invoiceId)
      .then(({ data }) => {
        if (cancelled || !data) return;
        const seen = new Set();
        const list = [];
        data.forEach(a => {
          const key = `${a.subjects?.name}-${a.subjects?.level}`;
          if (!seen.has(key) && a.subjects?.name) {
            seen.add(key);
            list.push({ name: a.subjects.name, level: a.subjects.level });
          }
        });
        setSubjects(list);
      });
    return () => { cancelled = true; };
  }, [invoiceId]);

  if (subjects === null) return <span className="text-[8px] text-slate-300 font-bold uppercase">Loading...</span>;
  if (subjects.length === 0) return <span className="text-[8px] text-slate-300 font-bold uppercase">—</span>;

  return (
    <div className="flex flex-col gap-1">
      {subjects.map((s, i) => (
        <div key={i} className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] font-black uppercase text-slate-700 dark:text-slate-200">{s.name}</span>
          {s.level && (
            <Badge variant="primary" className="text-[7px] uppercase font-black">{s.level}</Badge>
          )}
        </div>
      ))}
    </div>
  );
}